import React, { Dispatch, useEffect, useState } from 'react';
import { Box, Stack, FormControlLabel, Checkbox, Typography, Link } from '@mui/material';
import { classes } from './styles';
import {
  FormTextField,
  MultilineFormTextField
} from '../../../NewUI/Components/CustomUIElements/FormTextField';
import { ScoutCalendarAction, ScoutCalendarState } from '../../types';
import RecruitApiClient from '../../../RecruitApiClient';
import { useFeatureFlag } from '../../../FeatureFlags/useFeatureFlags';

export default function SecondStep({
  ScoutCalendarState,
  dispatch
}: {
  ScoutCalendarState: ScoutCalendarState;
  dispatch: Dispatch<ScoutCalendarAction>;
}) {
  const { createEventObject, errorsMessage } = ScoutCalendarState;
  const [hasVideoConferenceIntegration, setHasVideoConferenceIntegration] = useState(false);
  const [hasAutoEnabled, setHasAutoEnabled] = useState(false);
  const [provider, setProvider] = useState<string | null>(null);
  const hasVideoConferencePermission = useFeatureFlag('videoConferenceIntegrationEnabled');

  const getVideoConferenceLabel = (provider: string | null): string => {
    switch (provider) {
      case 'zoom':
        return 'Make it a Zoom Meeting';
      case 'google_meet':
        return 'Make it a Google Meeting';
      case 'teams':
        return 'Make it a Teams Meeting';
      default:
        return 'Enable Video Conferencing';
    }
  };

  useEffect(() => {
    RecruitApiClient.get<{ has_integration: boolean; provider?: string; expired?: boolean }>(
      '/api/v4/video_conferences/status'
    )
      .then(({ res }) => {
        setHasVideoConferenceIntegration(res.has_integration);
        setProvider(res.provider || null);
      })
      .catch((error) => {
        console.error('Failed to check video conference status:', error);
        setHasVideoConferenceIntegration(false);
      });
  }, []);

  useEffect(() => {
    if (
      hasVideoConferencePermission &&
      hasVideoConferenceIntegration &&
      createEventObject.eventType?.video_conferencing_default &&
      !hasAutoEnabled
    ) {
      dispatch({
        type: 'SET_CREATE_EVENT_OBJECT',
        payload: {
          ...createEventObject,
          enableVideoConferencing: true
        }
      });
      setHasAutoEnabled(true);
    }
  }, [
    hasVideoConferencePermission,
    hasVideoConferenceIntegration,
    createEventObject.eventType?.video_conferencing_default,
    hasAutoEnabled
  ]);

  return (
    <Box>
      <Box sx={classes.progressHeader}>Event Details</Box>
      <Stack sx={{ height: '455px', rowGap: 3 }}>
        <FormTextField
          label="Event name"
          value={createEventObject.eventName}
          onChange={(e) =>
            dispatch({
              type: 'SET_CREATE_EVENT_OBJECT',
              payload: { ...createEventObject, eventName: e.target.value }
            })
          }
          fullWidth
          required
          error={errorsMessage.eventName}
          maxLength={255}
          id="event-name-input"
        />
        <Box>
          <MultilineFormTextField
            label="Description"
            value={createEventObject.eventDescription}
            onChange={(e) =>
              dispatch({
                type: 'SET_CREATE_EVENT_OBJECT',
                payload: { ...createEventObject, eventDescription: e.target.value }
              })
            }
            fullWidth
            rows={6}
            placeholder="Write your additional notes or comments"
            maxLength={255}
            id="event-description-input"
          />
          <Stack sx={{ paddingRight: 1, flexDirection: 'row-reverse', color: '#838383' }}>
            {createEventObject.eventDescription.length}/255
          </Stack>
        </Box>
        {hasVideoConferencePermission && (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createEventObject.enableVideoConferencing}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_CREATE_EVENT_OBJECT',
                      payload: { ...createEventObject, enableVideoConferencing: e.target.checked }
                    })
                  }
                  disabled={!hasVideoConferenceIntegration}
                  sx={{
                    color: '#5BC4BF',
                    '&.Mui-checked': {
                      color: '#5BC4BF'
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{getVideoConferenceLabel(provider)}</Typography>
                  {!hasVideoConferenceIntegration && (
                    <Link
                      href="/admin/settings"
                      target="_blank"
                      sx={{ fontSize: '0.875rem', textDecoration: 'underline' }}
                    >
                      Setup Integration
                    </Link>
                  )}
                </Box>
              }
            />
            {!hasVideoConferenceIntegration && (
              <Typography variant="caption" sx={{ color: '#666', ml: 4, display: 'block' }}>
                Video conferencing integration not detected. Please set up your integration to
                enable this feature.
              </Typography>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );
}
